import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { DatePicker, ModalForm, Text } from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../hooks/useService";
import { isoToDate, localIsoDate } from "../../helpers/localDate";
import { formatBRL, formatCompetencia } from "../ow";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/**
 * Registro do pagamento de um cronograma: na aba Pagamentos do contrato, na lista
 * e no detalhe de pagamentos da empresa e na lista de notas fiscais (que busca o
 * cronograma da nota).
 *
 * Com nota, o pagamento é registrado nela primeiro (a nota vira "Paga"); depois o
 * cronograma passa a PAID com o valor esperado e a data escolhida.
 *
 * Quem abre confere antes se o cronograma tem valor (`scheduleHasValue`).
 *
 * Props:
 * - schedule: cronograma como vem da API (com `taxInvoice`, quando houver)
 * - onClose
 * - onPaid: depois de registrar (recarregar a lista)
 */
export default function MarkPaidModal({ schedule, onClose, onPaid }) {
  const paymentScheduleService = useService("payment-schedules");
  const taxInvoicesService = useService("taxInvoices");
  const [paymentDate, setPaymentDate] = useState(localIsoDate());
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  function close() {
    if (!loading) onClose();
  }

  async function confirm() {
    if (!paymentDate) {
      setError("Informe a data do pagamento");
      return;
    }
    setLoading(true);
    try {
      const nf = schedule.taxInvoice;
      if (nf?.id) {
        await taxInvoicesService.registerPayment(nf.id, paymentDate);
      }
      const result = await paymentScheduleService.updateStatus(schedule.id, {
        status: "PAID",
        actual_value: schedule.expected_value,
        payment_date: paymentDate,
      });
      if (result && !result.error) {
        toast.success("Pagamento registrado com sucesso!");
        setLoading(false);
        onPaid();
        return;
      }
      // Erro com { error: true } vem do sendError do service, que já mostrou o toast
      if (!result?.error) toast.error("Erro ao marcar como pago");
    } catch (err) {
      toast.error("Erro ao registrar pagamento");
    }
    setLoading(false);
  }

  const competence = { month: Number(schedule.reference_month), year: Number(schedule.reference_year) };

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={loading}
      $isLoading={loading}
      $size="medium"
      $title="Confirmar pagamento"
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: loading },
        { text: "Confirmar pagamento", action: confirm, variant: "primary", isDisabled: loading },
      ]}>
      <Content>
        <Text
          $text="Confirma que o pagamento de <strong>%s</strong> referente a <strong>%s</strong> foi realizado?"
          $replaces={[formatBRL(schedule.expected_value), formatCompetencia(competence, true)]}
          $variant="paragraph_small_medium"
          $color="N700"
          $colorEmphasis="N900"
        />
        <DatePicker
          $idForm="payment-date"
          $centralize
          $label="Data do pagamento"
          $variant="default"
          $isRequired
          $errors={error}
          $date={isoToDate(paymentDate)}
          $setDate={(date) => {
            setPaymentDate(localIsoDate(date) || "");
            if (error) setError("");
          }}
        />
      </Content>
    </ModalForm>
  );
}
